import { pages } from "@/Apps/Funnels/objects";
import { DropdownGroup } from "@/Apps/Pages/Properties/components/groups/DropdownGroup/DropdownGroup";
import {
  ColorPaletteIcon,
  defaultColorPalettes,
} from "@/Apps/Pages/Properties/components/sections/Appearance/Appearance";
import { unmapCampaignVariables } from "@/Apps/Pages/utils";
import Button from "@/ui/Button";
import Calout from "@/ui/Calout";
import DetailsView from "@/ui/DataView/DetailsView";
import Dropdown from "@/ui/Dropdown";
import Panel from "@/ui/Panel";
import { ButtonTypes, Variants } from "@/ui/types";
import { AIMessage } from "@langchain/core/messages";
import { PencilSimpleLine } from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import ReactJson from "react-json-view";

export const PageSpecFromStructuredMessage = (props: any) => {
  const {
    currentThread,
    setPinnedMessagesBottom,
    artifactOptions,
    setArtifactOptions,
  } = props;
  const payload = props.payload?.pageSpec || {};
  const [isEditing, setIsEditing] = useState(false);
  const [showFooter, setShowFooter] = useState(payload?.showFooter || false);
  // const [showFooter, setShowFooter] = useState(!payload?._fromInterrupt);

  const getBasicSpecData = (spec: any) => [
    {
      id: "companyName",
      label: "Company Name",
      value: spec?.settings?.companyName,
      fieldType: "string" as const,
    },
    {
      id: "goal",
      label: "Goal",
      value: spec?.goal,
      fieldType: "longtext" as const,
    },
    {
      id: "pageType",
      label: "Type of Page",
      value:
        pages.find((p) => p.pageType === spec?.pageType)?.name ||
        spec?.pageType,
      fieldType: "enum" as const,
      component: ({
        value,
        isEditable,
        onItemChange,
      }: {
        value: string | number | boolean;
        isEditable: boolean;
        onItemChange: (data: any) => void;
      }) => {
        const page = pages.find(
          (p) => p.pageType === String(spec?.pageType)
        );
        return (
          <p className="text-sm flex items-center gap-2">
            {page?.icon && (
              <div className="p-1 rounded bg-yellow-500 text-white">
                <page.icon size={16} />
              </div>
            )}
            {value}
          </p>
        );
      },
      options: pages.map((page) => ({
        label: page.name,
        value: page.pageType || "",
        icon: page.icon,
      })),
    },
    {
      id: "length",
      label: "Length of Page",
      value:
        spec?.length?.slice(0, 1).toUpperCase() + spec?.length?.slice(1),
      fieldType: "enum" as const,
      options: [
        { label: "Short (1-3 sections)", value: "Short" },
        { label: "Medium (4-6 sections)", value: "Medium" },
        { label: "Long (7+ sections)", value: "Long" },
      ],
    },
  ];

  const getDesignStyleData = (spec: any) => [
    {
      id: "colorPalette",
      label: "Color Palette",
      value: spec?.settings?.colorPalette,
      fieldType: "custom" as const,
      component: ({
        value,
        isEditable,
        onItemChange,
      }: {
        value: string | number | boolean;
        isEditable: boolean;
        onItemChange: (data: any) => void;
      }) => {
        const palette = defaultColorPalettes.find(
          (p) => p.id === String(value)
        );

        const colorItems = defaultColorPalettes.map((palette) => ({
          label: palette.label,
          id: palette.id,
          icon: <ColorPaletteIcon palette={palette} />,
        }));

        if (!palette) return <p>No palette found</p>;

        if (isEditable) {
          return (            
            <Dropdown
              label={
                colorItems.find((item) => item.id === value)?.label ||
                "Choose..."
              }
              items={colorItems}
              onClick={(key) =>
                onItemChange({
                  data: { 
                    id: "colorPalette",
                    value: colorItems[key].id,
                  },
                })
              }
            />
          );
        } else {
          return (
            <p className="text-sm flex items-center gap-2">
              <ColorPaletteIcon
                palette={palette}
                className="rounded-full p-2"
              />
              {palette?.label}
            </p>
          );
        }
      },
    },
    {
      id: "design",
      label: "Design Style",
      value: spec?.design,
      fieldType: "string" as const,
    },
    // {
    //   id: "logo",
    //   label: "Logo",
    //   value: spec?.settings?.logo,
    //   fieldType: "string" as const,
    // },
  ];

  const [newPageSpecData, setNewPageSpecData] = useState<any>([]);

  const [newPageSpec, setNewPageSpec] = useState<any>(payload || {});

  // Function to update parent's pageSpec state
  const updateParentPageSpec = (updatedSpec: any) => {
    // Apply color palette if colorPalette is selected
    let variables = {
      colors: {
        "inverseSurface": "#141414",
        "surfaceContainerHigh": "#202A37",
        "surfaceContainer": "#4B5562",
        "surfaceContainerLow": "#F9FAFB",
        "surface": "#FFFFFF",
        "primary": "#141414",
        "secondary": "#4B5562"
      },
      palette: "monochrome-black-gray",
      companyName: updatedSpec.settings?.companyName || "Company Name Test",
    };

    if (updatedSpec.settings?.colorPalette) {
      const selectedPalette = defaultColorPalettes.find(
        (p) => p.id === updatedSpec.settings.colorPalette
      );
      if (selectedPalette) {
        // Ensure all required colors are present
        const colors = {
          inverseSurface: selectedPalette.values.inverseSurface || "#141414",
          surfaceContainerHigh: selectedPalette.values.surfaceContainerHigh || "#202A37",
          surfaceContainer: selectedPalette.values.surfaceContainer || "#4B5562",
          surfaceContainerLow: selectedPalette.values.surfaceContainerLow || "#F9FAFB",
          surface: selectedPalette.values.surface || "#FFFFFF",
          primary: selectedPalette.values.primary || "#141414",
          secondary: selectedPalette.values.secondary || "#4B5562",
        };
        
        variables = {
          colors,
          palette: selectedPalette.id,
          companyName: updatedSpec.settings?.companyName || "Company Name Test",
        };
      }
    }

    const finalSpec = {
      variables: unmapCampaignVariables(variables),
    };

    // Update parent's pageSpec through artifactOptions
    if (setArtifactOptions) {
      setArtifactOptions({
        ...artifactOptions,
        pageSpec: finalSpec,
      });
    }
  };

  const handleBasicItemChange = async (id: string, allItems: any[]) => {
    const value = allItems.find((item: any) => item.id === id)?.value;
    // // setNewPageSpec({ ...newPageSpec, basic: { ...newPageSpec.basic, [id]: value }});
    // console.log({ id, allItems, value });

    let updatedSpec = { ...newPageSpec };
    if (id === "companyName")
      updatedSpec = {
        ...newPageSpec,
        settings: { ...(newPageSpec?.settings || {}), companyName: value },
      };
    if (id === "goal") updatedSpec = { ...newPageSpec, goal: value };
    if (id === "pageType") updatedSpec = { ...newPageSpec, pageType: value };
    if (id === "length") updatedSpec = { ...newPageSpec, length: value };

    setNewPageSpec(updatedSpec);
    updateParentPageSpec(updatedSpec);
  };

  const handleDesignItemChange = async (id: string, allItems: any[]) => {
    const value = allItems.find((item: any) => item.id === id)?.value;
    // setNewPageSpec({ ...newPageSpec, design: { ...newPageSpec.design, [id]: value }});

    let updatedSpec = { ...newPageSpec };
    if (id === "colorPalette")
      updatedSpec = {
        ...newPageSpec,
        settings: { ...(newPageSpec?.settings || {}), colorPalette: value },
      };
    if (id === "design") updatedSpec = { ...newPageSpec, design: value };
    if (id === "logo")
      updatedSpec = {
        ...newPageSpec,
        settings: { ...(newPageSpec?.settings || {}), logo: value },
      };

    setNewPageSpec(updatedSpec);
    updateParentPageSpec(updatedSpec);
  };

  const handleBuildPageAssets = async (
    e: React.MouseEvent<HTMLButtonElement>
  ) => {
    e.preventDefault();
    e.stopPropagation();

    setShowFooter(false);
    setPinnedMessagesBottom([]);

    // console.log("threadId", currentThread);
    console.log("payload", payload);
    console.log("thread values", currentThread.values);

    // currentThread.submit(undefined, {
    //   command: {
    //     resume: {
    //       ...currentThread.values,
    //       pageSpec: { ...payload, _fromInterrupt: true },
    //     },
    //   },
    // });

    // // works, but the values up to this point end at a tool call, so
    // // starting over with the tool call as the last message makes
    // // the LLM trigger an error
    // const messages = [
    //   // This spreads all messages except the last one (i.e., excludes the last message in the array)
    //   ...currentThread.values.messages.slice(0, currentThread.values.messages.length - 1),
    //   // new AIMessage("Building the page...")
    //   { type: "ai", content: "Building the page..." }
    // ];
    // console.log("messages to send", messages);

    // console.log("messages", currentThread.values.messages);

    console.log({ newPageSpec });

    // reset the artifact options (pageContent)
    setArtifactOptions({
      ...artifactOptions,
      isShowing: true,
      container: {
        ...artifactOptions.container,
        pageContent: [],
      },
    });

    await currentThread.submit(
      {
        ...currentThread.values,
        pageSpec: {
          ...(() => {
            const { showFooter, ...rest } = payload || {};
            return rest;
          })(),
          _fromInterrupt: true,
        },
      },
      {
        command: {
          update: {
            pageSpec: {
              ...(() => {
                const { showFooter, ...rest } = payload || {};
                return rest;
              })(),
              _fromInterrupt: true,
            },
          },
          resume: true,
        },
      }
    );

    // // attempting a state update
    // currentThread.submit(undefined, {
    //   command: {
    //     update: {
    //       pageSpec: { ...payload, _fromInterrupt: true },
    //     },
    //     resume: true,
    //   }
    // })
  };

  useEffect(() => {
    setNewPageSpecData([...getBasicSpecData(payload), ...getDesignStyleData(payload)]);
    setNewPageSpec(payload || {});
  }, [payload]);

  // Update data when newPageSpec changes
  useEffect(() => {
    setNewPageSpecData([...getBasicSpecData(newPageSpec), ...getDesignStyleData(newPageSpec)]);
  }, [newPageSpec]);

  return (
    <div className="flex flex-col gap-2 max-w-2xl">
      <Panel
        title="Landing Page Blueprint"
        className="w-full"
        actionButton={
          <Button
            type={ButtonTypes.SOFT}
            label={
              <>
                <PencilSimpleLine size={16} />
              </>
            }
            onClick={() => setIsEditing(!isEditing)}
          />
        }
      >
        <div className="flex flex-col gap-4">
          {/* <ReactJson src={newPageSpec} collapsed /> */}
          <Calout
            title="Page Specification"
            caption="This is the page specification that will be used to build the page"
          />
          <DetailsView
            title={<div className="mb-4">Basic Information</div>}
            item={{
              id: "page-spec",
              label: "Page Specification",
              data: newPageSpecData,
            }}
            visibleFields={["companyName", "goal", "pageType", "length"]}
            isEditable={isEditing}
            onItemChange={handleBasicItemChange}
          />
          <div className="h-px bg-gray-200 dark:bg-white/10" />

          <DetailsView
            title={<div className="mb-4">Design Style</div>}
            item={{
              id: "design-style",
              label: "Design Style",
              data: newPageSpecData,
            }}
            visibleFields={["colorPalette", "design", "logo"]}
            isEditable={isEditing}
            onItemChange={handleDesignItemChange}
          >
            {/* {(payload?.showFooter) && ( */}
            {showFooter && (
              <>
                <div className="h-px bg-gray-200 dark:bg-white/10 my-4" />
                <div
                  className={`flex mt-4 ${isEditing ? "justify-between" : "justify-end"}`}
                >
                  {isEditing && (
                    <Button
                      type={ButtonTypes.OUTLINED}
                      // variant={Variants.BLACK}
                      label="Cancel"
                      onClick={() => setIsEditing(false)}
                    />
                  )}
                  <Button
                    label="Build Landing Page"
                    type={ButtonTypes.ROUNDED}
                    variant={Variants.BLACK}
                    onClick={handleBuildPageAssets}
                  />
                </div>
              </>
            )}
            {/* )} */}
          </DetailsView>
        </div>
      </Panel>
    </div>
  );
};
